// useAnalytics.ts
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys";
import AnalyticsService from "@/integrations/service/analytics.service";
import { getVisitorId } from "@/lib/visitorId";
import { useI18n } from "@/lib/i18n";
import { langToApi } from "@/lib/enums";
import { useArticleBySlug } from "./useArticles";

export const useAnalyticsSummary = () => {
  return useQuery({
    queryKey: queryKeys.analyticsResume,
    queryFn: AnalyticsService.resume,
  });
};

export const useMyAnalyticsSummary = () => {
  return useQuery({
    queryKey: ["analytics-resume-mien"],
    queryFn: AnalyticsService.resumeMien,
  });
};

export const useArticleStats = (articleId?: number) => {
  return useQuery({
    queryKey: queryKeys.articleStats(articleId ?? 0),
    queryFn: () => AnalyticsService.statsArticle(articleId as number),
    enabled: !!articleId,
  });
};

const ARTICLE_ROUTE = /^\/article\/([^/]+)/;

const LECTURE_TIME_MS = 20_000;
const LECTURE_SCROLL_RATIO = 0.7;

/**
 * Enregistre une "vue" à chaque changement de page, et une "lecture" (une seule
 * fois par page) si le visiteur reste ≥20s ou scrolle ≥70% du contenu.
 */
export function usePageViewTracking() {
  const location = useLocation();
  const { locale } = useI18n();
  const lectureSentRef = useRef(false);

  const articleSlugMatch = ARTICLE_ROUTE.exec(location.pathname);
  const { data: articleDto } = useArticleBySlug(articleSlugMatch?.[1] ?? "");
  const articleId: number | undefined = articleSlugMatch ? articleDto?.id : undefined;

  useEffect(() => {
    // Attend la résolution de l'articleId avant d'enregistrer une vue sur une page article
    if (articleSlugMatch && !articleId) return;

    lectureSentRef.current = false;
    const sessionId = getVisitorId();
    const chemin = location.pathname;
    const langue = langToApi(locale);

    AnalyticsService.enregistrerVue({ chemin, langue, sessionId, articleId }).catch((e) => {
      console.error("Échec de l'enregistrement de la vue", e);
    });

    const sendLecture = () => {
      if (lectureSentRef.current) return;
      lectureSentRef.current = true;
      AnalyticsService.enregistrerLecture({ chemin, langue, sessionId, articleId }).catch((e) => {
        console.error("Échec de l'enregistrement de la lecture", e);
      });
    };

    const timer = setTimeout(sendLecture, LECTURE_TIME_MS);

    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const ratio = window.scrollY / scrollable;
      if (ratio >= LECTURE_SCROLL_RATIO) sendLecture();
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, articleId]);
}
